import React, { useCallback, useEffect, useRef, useState } from 'react'
import './navbar.scss'
import { Hamburger, CBLogoV2, Close, ExpandMoreIcon } from '../Icon'
import { Anchor } from '../Anchor'
import { Button } from '../Button'
import { useBreakpoint } from '../../utils/hooks'
import { LG } from '../../utils/constants'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../store'
import { userServices } from '../../../services'
import { resetConfiguration } from '../../../global-state/gbc-user-slice'
import { EngagementEvents, logCrmEvent } from '../../utils/crmEvents'
import {
  accessMeetsRequired,
  normalizePermissionsFromLogin,
  type AdminFeatureKey,
} from '../../permissions/adminFeatures'

const navGroups = [
  'Opportunity',
  'Blog & News',
  'Webinar',
  'FAQ',
  'Newsletter',
  'Unit Prices',
  'Settings',
  'Campaigns',
] as const

type NavLinkItem = {
  href: string
  text: string
  group: typeof navGroups[number]
  requiredFeature?: { feature: AdminFeatureKey; minLevel: 'read' | 'write' }
}

const navLinks: NavLinkItem[] = [
  {
    href: '/opportunities',
    text: 'Opportunities',
    group: 'Opportunity',
    requiredFeature: { feature: 'opportunities', minLevel: 'read' },
  },
  {
    href: '/opportunities/new',
    text: 'Add Opportunity',
    group: 'Opportunity',
    requiredFeature: { feature: 'opportunities', minLevel: 'write' },
  },
  {
    href: '/coming-soon',
    text: 'Coming soon',
    group: 'Opportunity',
    requiredFeature: { feature: 'comingSoon', minLevel: 'read' },
  },
  {
    href: '/coming-soon/new',
    text: 'Add coming soon',
    group: 'Opportunity',
    requiredFeature: { feature: 'comingSoon', minLevel: 'write' },
  },
  {
    href: '/orders',
    text: 'Orders',
    group: 'Opportunity',
    requiredFeature: { feature: 'orders', minLevel: 'read' },
  },
  {
    href: '/wallet/topups',
    text: 'Wallet top-ups',
    group: 'Opportunity',
    requiredFeature: { feature: 'investments', minLevel: 'read' },
  },
  {
    href: '/wallet/withdrawals',
    text: 'Wallet withdrawals',
    group: 'Opportunity',
    requiredFeature: { feature: 'withdrawals', minLevel: 'read' },
  },
  {
    href: '/trading',
    text: 'Stock trading',
    group: 'Opportunity',
    requiredFeature: { feature: 'investments', minLevel: 'read' },
  },
  {
    href: '/allocation-requests',
    text: 'Allocation Requests',
    group: 'Opportunity',
    requiredFeature: { feature: 'opportunities', minLevel: 'read' },
  },
  {
    href: '/investment-limit-requests',
    text: 'Investment Limit Requests',
    group: 'Opportunity',
    requiredFeature: { feature: 'kyc', minLevel: 'read' },
  },
  {
    href: '/secondary-market',
    text: 'Secondary Market',
    group: 'Opportunity',
    requiredFeature: { feature: 'secondaryMarket', minLevel: 'read' },
  },
  {
    href: '/clients',
    text: 'Clients',
    group: 'Opportunity',
    requiredFeature: { feature: 'investors', minLevel: 'read' },
  },
  {
    href: '/inquiries',
    text: 'Inquiries',
    group: 'Opportunity',
    requiredFeature: { feature: 'inquiries', minLevel: 'read' },
  },
  {
    href: '/business-inquiries',
    text: 'Business Inquiries',
    group: 'Opportunity',
    requiredFeature: { feature: 'businessInquiries', minLevel: 'read' },
  },
  {
    href: '/interest-payouts',
    text: 'Interest payout',
    group: 'Opportunity',
    requiredFeature: { feature: 'interestPayouts', minLevel: 'write' },
  },
  {
    href: '/dividend-payouts',
    text: 'Dividend payout',
    group: 'Opportunity',
    requiredFeature: { feature: 'dividendPayouts', minLevel: 'write' },
  },
  {
    href: '/blogs',
    text: 'Blogs',
    group: 'Blog & News',
    requiredFeature: { feature: 'blogs', minLevel: 'read' },
  },
  {
    href: '/blogs/new',
    text: 'Add Blog',
    group: 'Blog & News',
    requiredFeature: { feature: 'blogs', minLevel: 'write' },
  },
  {
    href: '/webinars',
    text: 'Webinars',
    group: 'Webinar',
    requiredFeature: { feature: 'webinars', minLevel: 'read' },
  },
  {
    href: '/webinars/new',
    text: 'Add Webinar',
    group: 'Webinar',
    requiredFeature: { feature: 'webinars', minLevel: 'write' },
  },
  {
    href: '/faqs',
    text: 'FAQs',
    group: 'FAQ',
    requiredFeature: { feature: 'faqs', minLevel: 'read' },
  },
  {
    href: '/faqs/new',
    text: 'Add FAQ',
    group: 'FAQ',
    requiredFeature: { feature: 'faqs', minLevel: 'write' },
  },
  {
    href: '/faqs/opportunity-questions',
    text: 'Opportunity FAQs',
    group: 'FAQ',
    requiredFeature: { feature: 'opportunities', minLevel: 'read' },
  },
  {
    href: '/unit-prices?type=general',
    text: 'General Unit Prices',
    group: 'Unit Prices',
    requiredFeature: { feature: 'unitPrices', minLevel: 'write' },
  },
  {
    href: '/unit-prices?type=client',
    text: 'Client Unit Prices',
    group: 'Unit Prices',
    requiredFeature: { feature: 'unitPrices', minLevel: 'write' },
  },
  {
    href: '/newsletter/subscribers',
    text: 'Newsletter Subscribers',
    group: 'Newsletter',
    requiredFeature: { feature: 'newsletter', minLevel: 'read' },
  },
  {
    href: '/newsletter/groups',
    text: 'Newsletter Groups',
    group: 'Newsletter',
    requiredFeature: { feature: 'newsletter', minLevel: 'read' },
  },
  {
    href: '/newsletter/groups/new',
    text: 'Add Newsletter Group',
    group: 'Newsletter',
    requiredFeature: { feature: 'newsletter', minLevel: 'write' },
  },
  {
    href: '/newsletter/send',
    text: 'Send Newsletter',
    group: 'Newsletter',
    requiredFeature: { feature: 'newsletter', minLevel: 'write' },
  },
  {
    href: '/news',
    text: 'News',
    group: 'Blog & News',
    requiredFeature: { feature: 'opportunities', minLevel: 'read' },
  },
  {
    href: '/news/schedule',
    text: 'News Fetch Schedule',
    group: 'Blog & News',
    requiredFeature: { feature: 'adminTeam', minLevel: 'write' },
  },
  {
    href: '/admin-team',
    text: 'Admin team',
    group: 'Settings',
    requiredFeature: { feature: 'adminTeam', minLevel: 'write' },
  },
  {
    href: '/account/change-password',
    text: 'Change password',
    group: 'Settings',
  },
  {
    href: '/campaigns',
    text: 'View Campaigns',
    group: 'Campaigns',
    requiredFeature: { feature: 'campaigns', minLevel: 'read' },
  },
  {
    href: '/campaigns/new',
    text: 'Create Campaign',
    group: 'Campaigns',
    requiredFeature: { feature: 'campaigns', minLevel: 'write' },
  },
]

export const Navbar = () => {
  const [isNavExpanded, setIsNavExpanded] = useState(false)
  const [unitPriceType, setUnitPriceType] = useState('')
  const location = useLocation()
  const { authToken, role, permissions } = useSelector((state: RootState) => state.gbcUser)
  const loggedIn = authToken && authToken
  const effectivePermissions = permissions ?? normalizePermissionsFromLogin(role as string, null)
  const linkVisible = (link: NavLinkItem) =>
    !link.requiredFeature ||
    accessMeetsRequired(
      effectivePermissions[link.requiredFeature.feature],
      link.requiredFeature.minLevel,
    )
  const { breakpoint } = useBreakpoint()
  const dispatch = useDispatch()
  const logoutHandler = async () => {
    const data: any = await userServices.userLogout({
      Authorization: authToken ? authToken : '',
    })
    if (data.status === 200) {
      dispatch(resetConfiguration())
      navigate('/opportunities')
      window.location.reload()
    }
  }
  const navigate = useNavigate()

  const handleCRMEvent = (pageName: string) => {
    let eventName = EngagementEvents.VISITED_OPPORTUNITIES_PAGE

    switch (pageName) {
      case 'Opportunities':
        eventName = EngagementEvents.VISITED_OPPORTUNITIES_PAGE
        break

      case 'Orders':
        eventName = EngagementEvents.VISITED_ORDERS_PAGE
        break
      case 'Clients':
        eventName = EngagementEvents.VISITED_INVESTORS_PAGE
        break
      case 'Inquiries':
        eventName = EngagementEvents.VISITED_INQUIRIES_PAGE
        break
      case 'Business Inquiries':
        eventName = EngagementEvents.VISITED_BUSINESS_INQUIRIES_PAGE
        break
      case 'Unit Prices':
        eventName = EngagementEvents.VISITED_UNIT_PRICES_PAGE
        break
      case 'Interest payout':
        eventName = EngagementEvents.VISITED_INTEREST_PAYOUTS_PAGE
        break
    }

    logCrmEvent(eventName)
  }

  useEffect(() => {
    if (!location.pathname.startsWith('/unit-prices')) {
      setUnitPriceType('')
      return
    }
    const params = new URLSearchParams(location.search)
    const type = params.get('type')
    setUnitPriceType(type || '')
  }, [location])

  // Dropdown Component.
  const DropDown = ({ text, subLinks }: { text: string; subLinks: NavLinkItem[] }) => {
    // Deps.
    const navigate = useNavigate()

    // Refs.
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    // States.
    const [dropdown, setDropdown] = useState(false)
    const [dropdownMobile, setDropdownMobile] = useState(false)

    // Effects.
    useEffect(() => {
      const handleClickOutside = (e: Event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
          setDropdown(false)
      }
      document.addEventListener('click', handleClickOutside)

      return () => {
        document.removeEventListener('click', handleClickOutside)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      }
    }, [dropdownRef])

    // Handlers.
    const clearTimeoutRef = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }, [])
    const handleMouseEnter = useCallback(() => {
      setDropdown(true)
      clearTimeoutRef()
    }, [clearTimeoutRef])
    const handleMouseLeave = useCallback(() => {
      timeoutRef.current = setTimeout(() => {
        setDropdown(false)
        clearTimeoutRef()
      }, 300)
    }, [clearTimeoutRef])

    // UI.
    return (
      <>
        {/* Desktop Dropdown */}
        <li
          className='nav-item dropdown-container'
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className='dropdown-trigger'>
            <Anchor href='#' text={text} variant='nav-link' />
            <ExpandMoreIcon
              classes={
                dropdown ? ['dropdown-trigger-icon', 'rotate-180'] : ['dropdown-trigger-icon']
              }
              height='16'
              width='16'
            />
          </div>

          <div ref={dropdownRef} className={`dropdown ${dropdown ? 'show' : ''}`}>
            <div className='dropdown-content'>
              {/* Sub Links. */}
              <div className='dropdown-item'>
                {subLinks.map(({ href, text }) => (
                  <div
                    onClick={() => {
                      if (text.includes('Unit Prices')) {
                        handleCRMEvent('Unit Prices')
                      } else {
                        handleCRMEvent(text)
                      }

                      if (isNavExpanded) setIsNavExpanded(false)
                      navigate(href)
                    }}
                    className='dropdown-item-link'
                    data-value={href}
                    key={href}
                  >
                    <div className='dropdown-item-link-text'>{text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </li>

        {/* Mobile Dropdown */}
        <li
          key={text}
          className='nav-item dropdown-mobile-container'
          onClick={(e) => {
            e.preventDefault()
            setDropdownMobile(!dropdownMobile)
          }}
        >
          <div className='dropdown-mobile-trigger'>
            <Anchor href='' text={text} variant='nav-link' />
            <ExpandMoreIcon
              classes={
                dropdownMobile ? ['dropdown-trigger-icon', 'rotate-180'] : ['dropdown-trigger-icon']
              }
              height='16'
              width='16'
            />
          </div>
          <div className={`dropdown-mobile ${dropdownMobile ? 'show' : ''}`}>
            <div className='dropdown-mobile-content'>
              {/* Sub Links */}
              <div className='dropdown-mobile-item'>
                {subLinks.map(({ href, text }) => (
                  <div
                    onClick={() => {
                      navigate(href)
                      setDropdownMobile(false)
                      if (isNavExpanded) setIsNavExpanded(false)
                    }}
                    className='dropdown-item-link'
                    data-value={href}
                    key={href}
                  >
                    <div className='dropdown-item-link-text'>{text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </li>
      </>
    )
  }

  return (
    <div className={isNavExpanded ? 'navigation-wrapper' : ''}>
      <nav className='navigation'>
        <Link to={'/opportunities'}>
          {' '}
          <CBLogoV2 />{' '}
        </Link>
        <button
          className='hamburger'
          onClick={() => {
            setIsNavExpanded(!isNavExpanded)
          }}
        >
          {!isNavExpanded && <Hamburger />}
          {isNavExpanded && <Close />}
        </button>
        <div className={isNavExpanded ? 'navigation-menu expanded' : 'navigation-menu'}>
          <ul className='nav-list'>
            <>
              {loggedIn &&
                navGroups.map((group) => {
                  const subLinks = navLinks.filter((x) => x.group === group).filter(linkVisible)
                  if (subLinks.length === 0) return null
                  return <DropDown key={group} text={group} subLinks={subLinks} />
                })}
              {loggedIn && (
                <>
                  <li className='nav-item'>
                    {!isNavExpanded ? (
                      <Button
                        text='Logout'
                        classes={
                          breakpoint && breakpoint > LG ? ['mr-8', 'navbar-btn-padding'] : ['mb-8']
                        }
                        variant='secondary-btn'
                        handleClick={logoutHandler}
                      />
                    ) : (
                      <div onClick={logoutHandler}>
                        <strong>
                          <Anchor href='' text='Logout' variant='nav-link-bold' />
                        </strong>{' '}
                      </div>
                    )}
                  </li>
                </>
              )}
            </>
          </ul>
        </div>
      </nav>
    </div>
  )
}
